<template>
  <!--
    EditorMediosGaleria — Brief Bunny Fase 1 (18 may 2026).
    Editor de la galeria mixta foto+video que se muestra en la ficha publica.

    Modelo (cada item de v-model):
      Imagen: { tipo: 'imagen', orden, url, alt, ratio, width, height }
      Video:  { tipo: 'video',  orden, streamId, posterUrl, durationSeconds,
                ratio: '9:16', width: 1080, height: 1920, titulo, descripcion }

    No toca Vuex: el padre (EditExperiencia.vue) recibe el array via input
    y dispatcha experienciaTypeChange({ name: 'medios', value: array }).
    Mantiene este componente reusable y testeable de forma aislada.
  -->
  <div class="editor-medios-galeria">
    <div class="editor-medios-galeria__intro">
      <p class="editor-medios-galeria__description">
        Esta es la galeria principal que se muestra en la ficha publica.
        Puedes mezclar fotos y videos verticales (reels). Los videos se alojan
        en Bunny Stream — sube tu MP4 al dashboard de Bunny y pega aqui el ID
        que te dieron.
      </p>
      <button
        type="button"
        class="editor-medios-galeria__help-toggle"
        @click="showInstructions = !showInstructions"
      >
        <i class="fas" :class="showInstructions ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
        {{ showInstructions ? 'Ocultar instrucciones' : 'Como subir un video a Bunny?' }}
      </button>
      <div v-if="showInstructions" class="editor-medios-galeria__instructions">
        <strong>Como subir un video a Bunny Stream</strong>
        <ol>
          <li>Entra a <a href="https://dash.bunny.net/stream" target="_blank" rel="noopener">https://dash.bunny.net/stream</a></li>
          <li>Selecciona la library <code>surreal-travel</code></li>
          <li>Click en <em>Upload Video</em> arriba a la derecha</li>
          <li>Sube tu MP4 (ideal: 1080x1920, 9:16, =&lt;45 segundos)</li>
          <li>Espera ~1-3 minutos a que Bunny termine de procesar (encoding)</li>
          <li>Cuando este listo, click en el video y copia el <em>Video GUID</em></li>
          <li>Pega ese GUID aca en el campo <em>Bunny videoId</em></li>
          <li>Listo. El video aparecera en la galeria del sitio.</li>
        </ol>
        <p class="editor-medios-galeria__instructions-note">
          Problemas? Contacta a Pablo (contacto@surreal.travel).
        </p>
      </div>
    </div>

    <!-- Lista de medios actuales -->
    <ul v-if="sortedMedios.length" class="editor-medios-galeria__list">
      <li
        v-for="(item, idx) in sortedMedios"
        :key="(item.tipo === 'imagen' ? item.url : item.streamId) + '-' + idx"
        class="editor-medios-galeria__item"
        :class="{ 'editor-medios-galeria__item--video': item.tipo === 'video' }"
      >
        <div class="editor-medios-galeria__handle" aria-hidden="true">
          <span>&#x22ee;&#x22ee;</span>
        </div>
        <div class="editor-medios-galeria__thumb">
          <img
            v-if="item.tipo === 'imagen' && item.url"
            :src="item.url"
            :alt="item.alt || 'Foto galeria'"
          />
          <img
            v-else-if="item.tipo === 'video' && item.posterUrl"
            :src="item.posterUrl"
            :alt="item.titulo || 'Poster video'"
          />
          <div v-else class="editor-medios-galeria__thumb-placeholder">
            <i :class="item.tipo === 'video' ? 'fas fa-play-circle' : 'fas fa-image'"></i>
          </div>
          <span class="editor-medios-galeria__type-badge">
            {{ item.tipo === 'imagen' ? 'foto' : 'video' }}
          </span>
        </div>
        <div class="editor-medios-galeria__info">
          <template v-if="item.tipo === 'imagen'">
            <div class="editor-medios-galeria__info-row">
              <strong>Foto</strong>
              <span class="editor-medios-galeria__info-meta">{{ item.ratio }} &middot; {{ item.width }}x{{ item.height }}</span>
            </div>
            <div class="editor-medios-galeria__info-alt">alt: {{ item.alt }}</div>
          </template>
          <template v-else>
            <div class="editor-medios-galeria__info-row">
              <strong>Video</strong>
              <span class="editor-medios-galeria__info-meta">{{ item.ratio || '9:16' }}<span v-if="item.durationSeconds"> &middot; {{ formatDuration(item.durationSeconds) }}</span></span>
            </div>
            <div class="editor-medios-galeria__info-alt">Bunny ID: <code>{{ item.streamId }}</code></div>
            <div v-if="item.titulo" class="editor-medios-galeria__info-alt">Titulo: {{ item.titulo }}</div>
          </template>
        </div>
        <div class="editor-medios-galeria__actions">
          <button
            type="button"
            class="editor-medios-galeria__btn editor-medios-galeria__btn--icon"
            :disabled="idx === 0"
            @click="moveUp(idx)"
            title="Subir"
          >&#9650;</button>
          <button
            type="button"
            class="editor-medios-galeria__btn editor-medios-galeria__btn--icon"
            :disabled="idx === sortedMedios.length - 1"
            @click="moveDown(idx)"
            title="Bajar"
          >&#9660;</button>
          <button
            type="button"
            class="editor-medios-galeria__btn"
            @click="startEdit(idx)"
          >Editar</button>
          <button
            type="button"
            class="editor-medios-galeria__btn editor-medios-galeria__btn--danger"
            @click="removeItem(idx)"
            :title="item.tipo === 'video' ? 'Eliminar de la galeria. El video sigue alojado en Bunny — borralo manualmente del dashboard si quieres liberar storage.' : 'Eliminar foto'"
          >Eliminar</button>
        </div>
      </li>
    </ul>
    <p v-else class="editor-medios-galeria__empty">
      No hay fotos ni videos en la galeria. Agrega abajo.
    </p>

    <!-- Botones de agregar -->
    <div v-if="!isFormOpen" class="editor-medios-galeria__add-bar">
      <!-- Upload directo desde la PC: input hidden + boton custom. Convierte
           JPG/PNG a WebP en el browser (optimizeImage util ya existente) y
           sube directo a Firebase Storage en la ruta de la experiencia. -->
      <button
        type="button"
        class="editor-medios-galeria__btn editor-medios-galeria__btn--primary"
        :disabled="atMaxMedios || uploading || !canUpload"
        @click="triggerFileUpload"
        :title="!canUpload
          ? 'Guarda la experiencia primero (necesitamos el ID para almacenar las fotos)'
          : 'Sube una o varias fotos desde tu PC. Se convierten a WebP automaticamente.'"
      >
        <i class="fas fa-upload"></i> Subir fotos desde mi PC
      </button>
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple
        style="display:none"
        @change="onFilesSelected"
      />
      <button
        type="button"
        class="editor-medios-galeria__btn"
        :disabled="atMaxMedios || uploading"
        @click="startAdd('imagen')"
        title="Pegar URL de imagen (avanzado)"
      ><i class="fas fa-link"></i> Agregar URL</button>
      <button
        type="button"
        class="editor-medios-galeria__btn editor-medios-galeria__btn--primary"
        :disabled="atMaxMedios || uploading"
        @click="startAdd('video')"
      ><i class="fas fa-plus"></i> Agregar video</button>
      <span v-if="atMaxMedios" class="editor-medios-galeria__max-warning">
        Maximo {{ MAX_MEDIOS }} medios por experiencia.
      </span>
      <span v-if="!canUpload && !atMaxMedios" class="editor-medios-galeria__hint">
        Para subir fotos primero guarda la experiencia.
      </span>
    </div>

    <!-- Progreso de upload (visible mientras hay fotos subiendo) -->
    <ul v-if="uploadQueue.length" class="editor-medios-galeria__upload-queue">
      <li
        v-for="item in uploadQueue"
        :key="item.id"
        class="editor-medios-galeria__upload-row"
        :class="{
          'editor-medios-galeria__upload-row--error': item.status === 'error',
          'editor-medios-galeria__upload-row--done': item.status === 'done',
        }"
      >
        <div class="editor-medios-galeria__upload-info">
          <span class="editor-medios-galeria__upload-name">{{ item.name }}</span>
          <span class="editor-medios-galeria__upload-status">
            <template v-if="item.status === 'optimizing'">Optimizando a WebP…</template>
            <template v-else-if="item.status === 'uploading'">Subiendo {{ Math.round(item.progress) }}%</template>
            <template v-else-if="item.status === 'done'">Listo</template>
            <template v-else-if="item.status === 'error'">Error: {{ item.error }}</template>
          </span>
        </div>
        <div class="editor-medios-galeria__upload-bar">
          <div
            class="editor-medios-galeria__upload-bar-fill"
            :style="{ width: (item.status === 'done' ? 100 : item.progress) + '%' }"
          ></div>
        </div>
      </li>
    </ul>

    <!-- Form: Agregar/Editar foto -->
    <div v-if="formMode === 'imagen'" class="editor-medios-galeria__form">
      <h6 class="editor-medios-galeria__form-title">
        {{ editingIndex !== null ? 'Editar foto' : 'Agregar foto' }}
      </h6>
      <div class="editor-medios-galeria__form-row">
        <label>URL de la imagen *</label>
        <input v-model="formImagen.url" type="url" placeholder="https://firebasestorage.googleapis.com/..." />
      </div>
      <div class="editor-medios-galeria__form-row">
        <label>Texto alt (SEO/accesibilidad) *</label>
        <input v-model="formImagen.alt" type="text" placeholder="Lodge Ni Newen - vista exterior" />
      </div>
      <div class="editor-medios-galeria__form-row editor-medios-galeria__form-row--inline">
        <div>
          <label>Ratio</label>
          <select v-model="formImagen.ratio">
            <option value="16:9">16:9</option>
            <option value="4:3">4:3</option>
            <option value="1:1">1:1</option>
            <option value="3:4">3:4</option>
          </select>
        </div>
        <div>
          <label>Ancho (px)</label>
          <input v-model.number="formImagen.width" type="number" min="0" />
        </div>
        <div>
          <label>Alto (px)</label>
          <input v-model.number="formImagen.height" type="number" min="0" />
        </div>
      </div>
      <p v-if="errors.length" class="editor-medios-galeria__error">
        {{ errors.join(' &middot; ') }}
      </p>
      <div class="editor-medios-galeria__form-actions">
        <button
          type="button"
          class="editor-medios-galeria__btn editor-medios-galeria__btn--primary"
          @click="confirmImagen"
        >Guardar foto</button>
        <button
          type="button"
          class="editor-medios-galeria__btn"
          @click="cancelForm"
        >Cancelar</button>
      </div>
    </div>

    <!-- Form: Agregar/Editar video -->
    <div v-if="formMode === 'video'" class="editor-medios-galeria__form">
      <h6 class="editor-medios-galeria__form-title">
        {{ editingIndex !== null ? 'Editar video' : 'Agregar video' }}
      </h6>
      <div class="editor-medios-galeria__form-row">
        <label>Bunny videoId (GUID) *</label>
        <input
          v-model="formVideo.streamId"
          type="text"
          placeholder="9f4d3a2b-1c5e-4b8a-9d2f-7c6a8e0b3f1d"
          spellcheck="false"
        />
        <small>El GUID lo encuentras en el dashboard de Bunny: click en el video, copia <em>Video GUID</em>.</small>
      </div>
      <div class="editor-medios-galeria__form-row">
        <label>Poster URL (opcional)</label>
        <input v-model="formVideo.posterUrl" type="url" placeholder="https://..." />
        <small>Si lo dejas vacio, el front usa el poster que Bunny genera automaticamente.</small>
      </div>
      <div class="editor-medios-galeria__form-row editor-medios-galeria__form-row--inline">
        <div>
          <label>Duracion (segundos)</label>
          <input v-model.number="formVideo.durationSeconds" type="number" min="0" />
        </div>
        <div class="editor-medios-galeria__form-fixed">
          <label>Ratio (fijo)</label>
          <input type="text" value="9:16" disabled readonly />
        </div>
      </div>
      <div class="editor-medios-galeria__form-row">
        <label>Titulo (opcional, para VideoObject schema)</label>
        <input v-model="formVideo.titulo" type="text" placeholder="Reel del lodge - exterior" />
      </div>
      <div class="editor-medios-galeria__form-row">
        <label>Descripcion corta (opcional, para SEO)</label>
        <input v-model="formVideo.descripcion" type="text" placeholder="Tour del lodge en 30 segundos" />
      </div>
      <p v-if="errors.length" class="editor-medios-galeria__error">
        {{ errors.join(' &middot; ') }}
      </p>
      <div class="editor-medios-galeria__form-actions">
        <button
          type="button"
          class="editor-medios-galeria__btn editor-medios-galeria__btn--primary"
          @click="confirmVideo"
        >Guardar video</button>
        <button
          type="button"
          class="editor-medios-galeria__btn"
          @click="cancelForm"
        >Cancelar</button>
      </div>
    </div>
  </div>
</template>

<script>
import { storage } from '@/firebase.service'
import { optimizeImage } from '@/store/utilities/imageOptimizer'

// Maximo de medios por experiencia (UX guard del brief §4).
const MAX_MEDIOS = 12

// Bunny Stream usa GUIDs estandar de 36 chars con dashes en posiciones 8-13-18-23.
// Regex laxo per brief: solo [a-f0-9-]{36}. Mas estricto seria
// /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i pero
// arriesga rechazar GUIDs validos si Bunny cambia el formato.
const GUID_REGEX = /^[a-f0-9-]{36}$/i

export default {
  name: 'EditorMediosGaleria',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    // Ids para construir la ruta de Firebase Storage donde van las fotos
    // subidas: zonas/{zonaId}/destinos/{destinoId}/experiencias/{experienciaId}/medios/...
    // Si experienciaId es null (experiencia nueva sin guardar), el upload se
    // bloquea y mostramos un hint para que Pablo guarde primero.
    zonaId: {
      type: String,
      default: null,
    },
    destinoId: {
      type: String,
      default: null,
    },
    experienciaId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      MAX_MEDIOS,
      showInstructions: false,
      // formMode: null | 'imagen' | 'video'. Cuando no es null hay form abierto
      // (sea para agregar o para editar — distinguido por editingIndex).
      formMode: null,
      // Indice del item siendo editado en sortedMedios. null = agregar nuevo.
      editingIndex: null,
      formImagen: this.makeEmptyImagen(),
      formVideo: this.makeEmptyVideo(),
      errors: [],
      // ─── Estado del upload de fotos (Brief Bunny Fase 2, iter Pablo) ───
      // uploadQueue es un array de { id, name, status, progress, error }
      // donde status ∈ 'optimizing'|'uploading'|'done'|'error'. Render-visible
      // mientras hay items que no esten 'done' (auto-limpieza al terminar).
      uploadQueue: [],
      uploading: false,
    }
  },
  computed: {
    medios() {
      return Array.isArray(this.value) ? this.value : []
    },
    // Ordenado por campo `orden`. Si no existe el campo (ej. import legacy)
    // el sort cae al orden natural del array.
    sortedMedios() {
      return [...this.medios].sort((a, b) => {
        const oa = Number(a && a.orden)
        const ob = Number(b && b.orden)
        if (Number.isNaN(oa) && Number.isNaN(ob)) return 0
        if (Number.isNaN(oa)) return 1
        if (Number.isNaN(ob)) return -1
        return oa - ob
      })
    },
    atMaxMedios() {
      return this.medios.length >= MAX_MEDIOS
    },
    isFormOpen() {
      return this.formMode !== null
    },
    // Habilita el boton de upload solo si tenemos todas las ids necesarias
    // para armar la ruta de Storage. Para experiencias nuevas (sin guardar)
    // experienciaId es null y bloqueamos hasta el primer save.
    canUpload() {
      return !!(this.zonaId && this.destinoId && this.experienciaId)
    },
  },
  methods: {
    makeEmptyImagen() {
      return { url: '', alt: '', ratio: '4:3', width: 1200, height: 900 }
    },
    makeEmptyVideo() {
      return {
        streamId: '',
        posterUrl: '',
        durationSeconds: null,
        titulo: '',
        descripcion: '',
      }
    },
    formatDuration(seconds) {
      const n = Number(seconds) || 0
      if (n <= 0) return ''
      const m = Math.floor(n / 60)
      const s = n % 60
      return `${m}:${String(s).padStart(2, '0')}`
    },
    // Reindex: reasigna `orden` 0..n-1 segun la posicion actual del item en
    // el array. Lo llamamos en cada cambio para mantener el invariante de
    // que `orden` es consecutivo y unico (requerimiento del brief §4.7).
    reindex(arr) {
      return arr.map((item, i) => ({ ...item, orden: i }))
    },
    emitArray(arr) {
      this.$emit('input', this.reindex(arr))
    },
    // ─── Reordenar ───
    moveUp(idx) {
      if (idx === 0) return
      const arr = [...this.sortedMedios]
      const tmp = arr[idx - 1]
      this.$set(arr, idx - 1, arr[idx])
      this.$set(arr, idx, tmp)
      this.emitArray(arr)
    },
    moveDown(idx) {
      const arr = [...this.sortedMedios]
      if (idx >= arr.length - 1) return
      const tmp = arr[idx + 1]
      this.$set(arr, idx + 1, arr[idx])
      this.$set(arr, idx, tmp)
      this.emitArray(arr)
    },
    // ─── Eliminar ───
    removeItem(idx) {
      const item = this.sortedMedios[idx]
      if (!item) return
      let msg
      if (item.tipo === 'video') {
        msg = `Eliminar el video con ID "${item.streamId}" de la galeria?\n\nEl video sigue alojado en Bunny — borralo manualmente del dashboard si quieres liberar storage.`
      } else {
        msg = `Eliminar la foto "${item.alt || item.url}" de la galeria?`
      }
      // eslint-disable-next-line no-alert
      if (!confirm(msg)) return
      const arr = this.sortedMedios.filter((_, i) => i !== idx)
      this.emitArray(arr)
    },
    // ─── Agregar ───
    startAdd(tipo) {
      if (this.atMaxMedios) return
      this.editingIndex = null
      this.formMode = tipo
      this.formImagen = this.makeEmptyImagen()
      this.formVideo = this.makeEmptyVideo()
      this.errors = []
    },
    // ─── Editar item existente ───
    startEdit(idx) {
      const item = this.sortedMedios[idx]
      if (!item) return
      this.editingIndex = idx
      this.formMode = item.tipo
      this.errors = []
      if (item.tipo === 'imagen') {
        this.formImagen = {
          url: item.url || '',
          alt: item.alt || '',
          ratio: item.ratio || '4:3',
          width: Number(item.width) || 1200,
          height: Number(item.height) || 900,
        }
      } else {
        this.formVideo = {
          streamId: item.streamId || '',
          posterUrl: item.posterUrl || '',
          durationSeconds: Number(item.durationSeconds) || null,
          titulo: item.titulo || '',
          descripcion: item.descripcion || '',
        }
      }
    },
    cancelForm() {
      this.formMode = null
      this.editingIndex = null
      this.formImagen = this.makeEmptyImagen()
      this.formVideo = this.makeEmptyVideo()
      this.errors = []
    },
    // ─── Validacion ───
    validateImagen() {
      const errs = []
      const f = this.formImagen
      if (!f.url || !String(f.url).trim()) errs.push('URL requerida')
      if (!f.alt || !String(f.alt).trim()) errs.push('Alt text requerido (SEO)')
      if (!['16:9', '4:3', '1:1', '3:4'].includes(f.ratio)) errs.push('Ratio invalido')
      if (Number(f.width) <= 0 || Number(f.height) <= 0) errs.push('Width y height deben ser > 0')
      return errs
    },
    validateVideo() {
      const errs = []
      const f = this.formVideo
      const sid = (f.streamId || '').trim()
      if (!sid) {
        errs.push('Bunny videoId requerido')
      } else if (!GUID_REGEX.test(sid)) {
        errs.push('Bunny videoId no parece un GUID valido (formato esperado: 36 chars hex con dashes)')
      }
      // Si ya existe otro video con el mismo streamId, error (evita duplicados).
      const dup = this.sortedMedios.find((it, i) => {
        if (this.editingIndex !== null && i === this.editingIndex) return false
        return it.tipo === 'video' && (it.streamId || '').trim() === sid
      })
      if (dup) errs.push('Ya existe un video con ese ID en la galeria')
      return errs
    },
    // ─── Confirmar ───
    confirmImagen() {
      const errs = this.validateImagen()
      this.errors = errs
      if (errs.length) return
      const newItem = {
        tipo: 'imagen',
        url: this.formImagen.url.trim(),
        alt: this.formImagen.alt.trim(),
        ratio: this.formImagen.ratio,
        width: Number(this.formImagen.width) || 1200,
        height: Number(this.formImagen.height) || 900,
        // orden se reasigna en reindex() despues. Aca damos un placeholder
        // razonable (la posicion final o la existente al editar).
        orden:
          this.editingIndex !== null
            ? this.sortedMedios[this.editingIndex].orden
            : this.sortedMedios.length,
      }
      const arr = this.editingIndex !== null
        ? this.sortedMedios.map((it, i) => (i === this.editingIndex ? newItem : it))
        : [...this.sortedMedios, newItem]
      this.emitArray(arr)
      this.cancelForm()
    },
    confirmVideo() {
      const errs = this.validateVideo()
      this.errors = errs
      if (errs.length) return
      const newItem = {
        tipo: 'video',
        streamId: this.formVideo.streamId.trim(),
        posterUrl: (this.formVideo.posterUrl || '').trim(),
        durationSeconds: Number(this.formVideo.durationSeconds) || 0,
        // Ratio + dimensiones fijos para reels verticales (brief §3).
        ratio: '9:16',
        width: 1080,
        height: 1920,
        titulo: (this.formVideo.titulo || '').trim(),
        descripcion: (this.formVideo.descripcion || '').trim(),
        orden:
          this.editingIndex !== null
            ? this.sortedMedios[this.editingIndex].orden
            : this.sortedMedios.length,
      }
      const arr = this.editingIndex !== null
        ? this.sortedMedios.map((it, i) => (i === this.editingIndex ? newItem : it))
        : [...this.sortedMedios, newItem]
      this.emitArray(arr)
      this.cancelForm()
    },

    // ─── Upload de fotos desde la PC (iter UX Pablo 19 may 2026) ─────────
    // Flow:
    //   1. Click en boton "Subir fotos" -> abre file picker (multiple)
    //   2. Por cada file: optimizeImage (JPG/PNG -> WebP, resize a 1600px max)
    //   3. Upload a Firebase Storage en la ruta de la experiencia
    //   4. Get downloadURL + push al array `medios[]` con shape de imagen
    //   5. Auto-detecta ratio mas cercano (16:9 / 4:3 / 1:1 / 3:4) segun dims
    triggerFileUpload() {
      if (!this.canUpload || this.atMaxMedios) return
      const input = this.$refs.fileInput
      if (input) input.click()
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files || [])
      if (event.target) event.target.value = ''
      if (!files.length) return
      // Limit a lo que falte para no superar MAX_MEDIOS
      const slotsDisponibles = MAX_MEDIOS - this.medios.length
      const toUpload = files.slice(0, slotsDisponibles)
      if (files.length > slotsDisponibles) {
        // eslint-disable-next-line no-alert
        alert(`Maximo ${MAX_MEDIOS} medios por experiencia. Subiendo solo las primeras ${slotsDisponibles} fotos.`)
      }
      this.uploadFiles(toUpload)
    },
    async uploadFiles(files) {
      this.uploading = true
      try {
        // Procesamos secuencial para no saturar la red en mobile + tener
        // progreso claro por archivo. Para batches grandes se podria
        // paralelizar con limite de concurrencia.
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          // eslint-disable-next-line no-await-in-loop
          await this.processSingleFile(file)
        }
      } finally {
        this.uploading = false
        // Auto-limpiar items 'done' del queue tras 3 segundos para que la
        // UI vuelva a estado limpio (los errores quedan para revision).
        setTimeout(() => {
          this.uploadQueue = this.uploadQueue.filter(it => it.status !== 'done')
        }, 3000)
      }
    },
    async processSingleFile(file) {
      const id = 'up-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8)
      const queueItem = {
        id,
        name: file.name || 'archivo',
        status: 'optimizing',
        progress: 0,
        error: null,
      }
      this.uploadQueue.push(queueItem)
      try {
        // Step 1: optimizar (JPG/PNG -> WebP + resize). Si el archivo ya es
        // WebP o no es convertible (GIF, SVG), optimizeImage devuelve el
        // file original con su extension.
        const opt = await optimizeImage(file, { maxWidth: 1600, quality: 0.85 })
        if (!opt || !opt.blob) throw new Error('No se pudo optimizar la imagen')

        // Step 2: medir dimensiones reales del blob optimizado para
        // popular width/height y deducir ratio.
        const dims = await this.measureBlob(opt.blob)

        // Step 3: subir a Firebase Storage
        queueItem.status = 'uploading'
        const fileName = Math.floor(Math.random() * 100000000) + opt.extension
        const path = `zonas/${this.zonaId}/destinos/${this.destinoId}/experiencias/${this.experienciaId}/medios/${fileName}`
        const storageRef = storage.ref().child(path)
        const uploadTask = storageRef.put(opt.blob, { contentType: opt.blob.type || 'image/webp' })

        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snap) => {
              const pct = snap.totalBytes > 0
                ? (snap.bytesTransferred / snap.totalBytes) * 100
                : 0
              queueItem.progress = pct
            },
            (err) => reject(err),
            () => resolve()
          )
        })

        // Step 4: get download URL + push al array medios
        const url = await uploadTask.snapshot.ref.getDownloadURL()
        const newItem = {
          tipo: 'imagen',
          url,
          alt: (file.name || 'foto').replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || 'foto',
          ratio: this.closestRatio(dims.width, dims.height),
          width: dims.width,
          height: dims.height,
          orden: this.sortedMedios.length,
        }
        const arr = [...this.sortedMedios, newItem]
        this.emitArray(arr)

        queueItem.status = 'done'
        queueItem.progress = 100
      } catch (err) {
        queueItem.status = 'error'
        queueItem.error = (err && err.message) || 'Error desconocido'
        // eslint-disable-next-line no-console
        console.error('[EditorMediosGaleria] upload fallo:', err)
      }
    },
    // Mide width/height de un blob de imagen via Image() en memoria.
    measureBlob(blob) {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(blob)
        const img = new Image()
        img.onload = () => {
          URL.revokeObjectURL(url)
          resolve({ width: img.width || 1200, height: img.height || 900 })
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          resolve({ width: 1200, height: 900 })
        }
        img.src = url
      })
    },
    // Mapea dimensiones a uno de los 4 ratios del enum del brief, eligiendo
    // el mas cercano por diferencia absoluta del aspect ratio.
    closestRatio(width, height) {
      const ar = (Number(width) || 1) / (Number(height) || 1)
      const candidatos = [
        { name: '16:9', val: 16 / 9 },
        { name: '4:3', val: 4 / 3 },
        { name: '1:1', val: 1 },
        { name: '3:4', val: 3 / 4 },
      ]
      let best = candidatos[0]
      let bestDiff = Math.abs(ar - candidatos[0].val)
      for (let i = 1; i < candidatos.length; i++) {
        const diff = Math.abs(ar - candidatos[i].val)
        if (diff < bestDiff) {
          bestDiff = diff
          best = candidatos[i]
        }
      }
      return best.name
    },
  },
}
</script>

<style scoped>
.editor-medios-galeria {
  border: 1px solid #d0d7de;
  border-radius: 8px;
  padding: 16px;
  background: #fafbfc;
}

.editor-medios-galeria__intro {
  margin-bottom: 12px;
}
.editor-medios-galeria__description {
  font-size: 13px;
  color: #4a4a4a;
  margin: 0 0 8px;
  line-height: 1.45;
}
.editor-medios-galeria__help-toggle {
  background: transparent;
  border: 0;
  color: #2c7be5;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 0;
  font-weight: 600;
}
.editor-medios-galeria__help-toggle:hover { text-decoration: underline; }
.editor-medios-galeria__help-toggle .fas { margin-right: 6px; }

.editor-medios-galeria__instructions {
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  padding: 12px 14px;
  margin: 8px 0 0;
  font-size: 13px;
  color: #333;
}
.editor-medios-galeria__instructions ol {
  padding-left: 18px;
  margin: 6px 0;
}
.editor-medios-galeria__instructions li { margin-bottom: 4px; }
.editor-medios-galeria__instructions code {
  background: #e9ecef;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}
.editor-medios-galeria__instructions-note {
  font-size: 12px;
  color: #666;
  margin: 8px 0 0;
}

.editor-medios-galeria__list {
  list-style: none;
  padding: 0;
  margin: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.editor-medios-galeria__item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  padding: 10px 12px;
}
.editor-medios-galeria__item--video { border-left: 3px solid #2c7be5; }

.editor-medios-galeria__handle {
  color: #999;
  font-size: 20px;
  line-height: 1;
  user-select: none;
}

.editor-medios-galeria__thumb {
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f0f0;
}
.editor-medios-galeria__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.editor-medios-galeria__thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #888;
  font-size: 24px;
}
.editor-medios-galeria__type-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
}

.editor-medios-galeria__info {
  flex: 1;
  min-width: 0;
}
.editor-medios-galeria__info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}
.editor-medios-galeria__info-row strong {
  font-size: 13px;
  color: #1f2328;
}
.editor-medios-galeria__info-meta {
  font-size: 11px;
  color: #888;
}
.editor-medios-galeria__info-alt {
  font-size: 12px;
  color: #555;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.editor-medios-galeria__info-alt code {
  background: #f0f0f0;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
}

.editor-medios-galeria__actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.editor-medios-galeria__empty {
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 24px 12px;
  background: #f6f8fa;
  border: 1px dashed #d0d7de;
  border-radius: 6px;
  margin: 12px 0;
  font-size: 13px;
}

.editor-medios-galeria__add-bar {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
}
.editor-medios-galeria__max-warning {
  font-size: 12px;
  color: #c4351c;
  margin-left: 6px;
}
.editor-medios-galeria__hint {
  font-size: 12px;
  color: #888;
  font-style: italic;
  margin-left: 6px;
}

/* Lista de progreso de uploads — visible mientras subimos fotos */
.editor-medios-galeria__upload-queue {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.editor-medios-galeria__upload-row {
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.editor-medios-galeria__upload-row--error { border-color: #c4351c; background: #fff4f2; }
.editor-medios-galeria__upload-row--done  { border-color: #2da44e; background: #f0faf2; }
.editor-medios-galeria__upload-info {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 12px;
}
.editor-medios-galeria__upload-name {
  font-weight: 600;
  color: #1f2328;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.editor-medios-galeria__upload-status {
  color: #666;
  flex-shrink: 0;
}
.editor-medios-galeria__upload-row--error .editor-medios-galeria__upload-status { color: #c4351c; }
.editor-medios-galeria__upload-row--done .editor-medios-galeria__upload-status { color: #2da44e; }
.editor-medios-galeria__upload-bar {
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
}
.editor-medios-galeria__upload-bar-fill {
  height: 100%;
  background: #2c7be5;
  transition: width 0.2s ease;
}
.editor-medios-galeria__upload-row--done .editor-medios-galeria__upload-bar-fill { background: #2da44e; }
.editor-medios-galeria__upload-row--error .editor-medios-galeria__upload-bar-fill { background: #c4351c; }

/* Buttons */
.editor-medios-galeria__btn {
  background: #fff;
  border: 1px solid #d0d7de;
  color: #1f2328;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.editor-medios-galeria__btn:hover:not(:disabled) {
  background: #f0f3f6;
  border-color: #a3acb5;
}
.editor-medios-galeria__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.editor-medios-galeria__btn--icon {
  padding: 6px 8px;
  min-width: 28px;
}
.editor-medios-galeria__btn--primary {
  background: #2c7be5;
  color: #fff;
  border-color: #2c7be5;
}
.editor-medios-galeria__btn--primary:hover:not(:disabled) {
  background: #1c66c9;
  border-color: #1c66c9;
}
.editor-medios-galeria__btn--danger {
  color: #c4351c;
  border-color: #e2c4be;
}
.editor-medios-galeria__btn--danger:hover:not(:disabled) {
  background: #fff4f2;
  border-color: #c4351c;
}

/* Forms */
.editor-medios-galeria__form {
  background: #fff;
  border: 1px solid #2c7be5;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 10px;
}
.editor-medios-galeria__form-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #1f2328;
}
.editor-medios-galeria__form-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}
.editor-medios-galeria__form-row label {
  font-size: 12px;
  font-weight: 600;
  color: #4a4a4a;
  margin-bottom: 4px;
}
.editor-medios-galeria__form-row input,
.editor-medios-galeria__form-row select {
  border: 1px solid #d0d7de;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  font-family: inherit;
  color: #1f2328;
  background: #fff;
}
.editor-medios-galeria__form-row input:focus,
.editor-medios-galeria__form-row select:focus {
  outline: none;
  border-color: #2c7be5;
  box-shadow: 0 0 0 2px rgba(44, 123, 229, 0.15);
}
.editor-medios-galeria__form-row small {
  font-size: 11px;
  color: #666;
  margin-top: 4px;
}
.editor-medios-galeria__form-row--inline {
  flex-direction: row;
  gap: 12px;
}
.editor-medios-galeria__form-row--inline > div {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.editor-medios-galeria__form-fixed input[disabled] {
  background: #f0f3f6;
  color: #666;
  cursor: not-allowed;
}

.editor-medios-galeria__error {
  background: #fff4f2;
  border: 1px solid #c4351c;
  color: #c4351c;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  margin: 8px 0;
}
.editor-medios-galeria__form-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

@media (max-width: 600px) {
  .editor-medios-galeria__item {
    flex-wrap: wrap;
  }
  .editor-medios-galeria__info {
    width: 100%;
  }
  .editor-medios-galeria__actions {
    width: 100%;
    justify-content: flex-end;
  }
  .editor-medios-galeria__form-row--inline {
    flex-direction: column;
  }
}
</style>
